import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { lessonSubscriptionsApi } from '@/api/lessonSubscriptions'
import { groupPricesApi } from '@/api/groupPrices'
import { groupsApi } from '@/api/groups'
import type {
  GroupLessonPrice,
  GroupPriceDisplay,
  GroupSubscriptionBatch,
  GroupSubscriptionBatchDetail,
  LessonPaymentMethod,
  LessonSubscriptionGrantDistribution,
  LessonSubscriptionPlan,
} from '@/types/billing'
import { formatMoney } from './formatMoney'
import { getErrorMessage } from '@/utils/error'

type Props = {
  groupId: number
}

const ISSUE_METHODS: LessonPaymentMethod[] = ['from_balance', 'cash', 'deferred']

export function GroupFinanceSection({ groupId }: Props) {
  const { t } = useTranslation()
  const [batches, setBatches] = useState<GroupSubscriptionBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [issueOpen, setIssueOpen] = useState(false)
  const [issueLoading, setIssueLoading] = useState(false)
  const [issueLoadError, setIssueLoadError] = useState<string | null>(null)
  const [plans, setPlans] = useState<LessonSubscriptionPlan[]>([])
  const [issuePlanId, setIssuePlanId] = useState<number | ''>('')
  const [issueStartsAt, setIssueStartsAt] = useState('')
  const [issueMethod, setIssueMethod] = useState<LessonPaymentMethod>('from_balance')
  const [submitting, setSubmitting] = useState(false)
  const [batchDetail, setBatchDetail] = useState<GroupSubscriptionBatchDetail | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailLoadError, setDetailLoadError] = useState<string | null>(null)

  // Цена часа группы: своя ступень между индивидуальной ценой участника и
  // базовой ставкой учителя. До неё групповой урок считался по ставке учителя.
  const [price, setPrice] = useState<GroupLessonPrice | null>(null)
  const [priceHistory, setPriceHistory] = useState<GroupLessonPrice[]>([])
  const [priceDisplay, setPriceDisplay] = useState<GroupPriceDisplay>('own_share')
  const [priceLoading, setPriceLoading] = useState(true)
  const [priceLoadError, setPriceLoadError] = useState<string | null>(null)
  const [priceDialogOpen, setPriceDialogOpen] = useState(false)
  const [priceValue, setPriceValue] = useState('')
  const [priceError, setPriceError] = useState<string | null>(null)
  const [priceSaving, setPriceSaving] = useState(false)

  const loadPrice = useCallback(async () => {
    setPriceLoading(true)
    setPriceLoadError(null)
    try {
      const data = await groupPricesApi.index(groupId)
      setPrice(data.active)
      setPriceHistory(data.history)
      setPriceDisplay(data.price_display)
    } catch (err) {
      // Не в общий алерт секции: там сообщение стирает любой перезапрос
      // абонементов, а «цена не загрузилась» и «цены нет» — разные вещи.
      setPriceLoadError(getErrorMessage(err))
    } finally {
      setPriceLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    void loadPrice()
  }, [loadPrice])

  const savePrice = async () => {
    // Запятая вместо точки: браузер её в type="number" не отдаёт, но поле
    // читается и из вставки. Экспонента и минус отсекаются здесь же — раньше
    // «Сохранить» на них молча ничего не делал, и учитель не понимал, почему.
    const amount = Number(priceValue.replace(',', '.'))
    if (!Number.isFinite(amount) || amount < 0) {
      setPriceError(t('billing.priceInvalid'))

      return
    }

    setPriceSaving(true)
    setPriceError(null)
    try {
      await groupPricesApi.store(groupId, { price_per_hour: amount })
      closePriceDialog()
      await loadPrice()
    } catch (err) {
      // В диалоге, а не в алерте секции: секция под подложкой, и сообщение
      // оставалось невидимым.
      setPriceError(getErrorMessage(err))
    } finally {
      setPriceSaving(false)
    }
  }

  const closePriceDialog = () => {
    setPriceDialogOpen(false)
    setPriceValue('')
    setPriceError(null)
  }

  const changePriceDisplay = async (next: GroupPriceDisplay) => {
    const previous = priceDisplay
    setPriceDisplay(next)
    try {
      await groupsApi.update(groupId, { price_display: next })
    } catch (err) {
      setPriceDisplay(previous)
      setError(getErrorMessage(err))
    }
  }
  const [loadingBatchId, setLoadingBatchId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await lessonSubscriptionsApi.listForGroup(groupId)
      setBatches(data.batches ?? [])
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [groupId, t])

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  const openIssue = async () => {
    setIssueOpen(true)
    setIssueLoading(true)
    setIssueLoadError(null)
    setPlans([])
    setIssuePlanId('')
    try {
      const res = await lessonSubscriptionsApi.listPlans({
        is_active: true,
        subscription_type: 'group',
      })
      setPlans(res.data)
      setIssuePlanId(res.data[0]?.id ?? '')
      setIssueStartsAt(new Date().toISOString().slice(0, 10))
    } catch (e: unknown) {
      setIssueLoadError(getErrorMessage(e) || t('common.error'))
    } finally {
      setIssueLoading(false)
    }
  }

  const closeIssueDialog = () => {
    if (submitting) return
    setIssueOpen(false)
    setIssueLoading(false)
    setIssueLoadError(null)
  }

  const handleIssue = async () => {
    if (!issuePlanId || !issueStartsAt) return
    setSubmitting(true)
    try {
      await lessonSubscriptionsApi.issueForGroup(groupId, {
        plan_id: Number(issuePlanId),
        starts_at: issueStartsAt,
        payment_method: issueMethod,
      })
      setIssueOpen(false)
      await load()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const openBatchDetail = async (batchId: string) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailLoadError(null)
    setBatchDetail(null)
    setLoadingBatchId(batchId)
    try {
      const detail = await lessonSubscriptionsApi.getGroupBatch(groupId, batchId)
      setBatchDetail(detail)
    } catch (e: unknown) {
      setDetailLoadError(getErrorMessage(e) || t('common.error'))
    } finally {
      setDetailLoading(false)
      setLoadingBatchId(null)
    }
  }

  const closeDetailDialog = () => {
    setDetailOpen(false)
    setDetailLoading(false)
    setDetailLoadError(null)
    setBatchDetail(null)
    setLoadingBatchId(null)
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            {t('billing.financeSection', 'Финансы')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => void openIssue()}
            disabled={issueLoading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('billing.issueGroupSubscription', 'Выдать групповой абонемент')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Цена часа занятия: по ней считается часть каждого участника, а
            сумма урока складывается из частей. Индивидуальная цена участника
            перебивает групповую. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1">{t('billing.groupPriceTitle')}</Typography>
              {priceLoading ? (
                <Skeleton variant="text" width={160} />
              ) : priceLoadError ? (
                <Typography variant="body2" color="error">
                  {priceLoadError}
                </Typography>
              ) : (
                <Typography variant="h6">
                  {price
                    ? t('billing.groupPricePerHour', {
                        amount: formatMoney(price.price_per_hour, price.currency),
                      })
                    : t('billing.groupPriceNotSet')}
                </Typography>
              )}
            </Box>
            <Button variant="outlined" onClick={() => setPriceDialogOpen(true)}>
              {price ? t('billing.groupPriceChange') : t('billing.groupPriceSet')}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('billing.groupPriceHint')}
          </Typography>

          <FormControl size="small" sx={{ mt: 2, minWidth: 260 }} disabled={priceLoading}>
            <InputLabel>{t('billing.groupPriceDisplayLabel')}</InputLabel>
            <Select
              label={t('billing.groupPriceDisplayLabel')}
              value={priceDisplay}
              onChange={(event) => void changePriceDisplay(event.target.value as GroupPriceDisplay)}
            >
              <MenuItem value="own_share">{t('billing.groupPriceDisplay.own_share')}</MenuItem>
              <MenuItem value="lesson_total">{t('billing.groupPriceDisplay.lesson_total')}</MenuItem>
            </Select>
          </FormControl>

          {priceHistory.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('billing.groupPriceHistory', { count: priceHistory.length })}
            </Typography>
          )}
        </Paper>


        {loading ? (
          <Paper>
            <Skeleton variant="rectangular" width="100%" height={120} />
          </Paper>
        ) : batches.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('billing.noGroupSubscriptions', 'Групповые абонементы не выдавались')}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.plan', 'Абонемент')}</TableCell>
                  <TableCell>{t('billing.startsAt', 'Начало')}</TableCell>
                  <TableCell>{t('billing.participants', 'Участники')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.batch_id}>
                    <TableCell>{batch.plan?.title ?? '—'}</TableCell>
                    <TableCell>{batch.starts_at ?? '—'}</TableCell>
                    <TableCell>{batch.participants_count}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={loadingBatchId === batch.batch_id}
                        onClick={() => void openBatchDetail(batch.batch_id)}
                      >
                        {t('billing.distribution', 'Распределение')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={issueOpen} onClose={closeIssueDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.issueGroupSubscription', 'Выдать групповой абонемент')}</DialogTitle>
        <DialogContent>
          {issueLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Box>
          ) : issueLoadError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {issueLoadError}
            </Alert>
          ) : plans.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('billing.noGroupPlans', 'Нет доступных групповых абонементов')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('billing.plan', 'Абонемент')}</InputLabel>
                <Select
                  label={t('billing.plan', 'Абонемент')}
                  value={issuePlanId}
                  onChange={(e) => setIssuePlanId(Number(e.target.value))}
                >
                  {plans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label={t('billing.startsAt', 'Начало')}
                InputLabelProps={{ shrink: true }}
                value={issueStartsAt}
                onChange={(e) => setIssueStartsAt(e.target.value)}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>{t('billing.paymentMethod', 'Способ оплаты')}</InputLabel>
                <Select
                  label={t('billing.paymentMethod', 'Способ оплаты')}
                  value={issueMethod}
                  onChange={(e) => setIssueMethod(e.target.value as LessonPaymentMethod)}
                >
                  {ISSUE_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {t(`billing.paymentMethodOption.${m}`, m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIssueDialog} disabled={submitting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button
            variant="contained"
            disabled={submitting || issueLoading || !!issueLoadError || plans.length === 0}
            onClick={() => void handleIssue()}
          >
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={closeDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t('billing.distributionTitle', 'Распределение абонемента')}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
            </Box>
          ) : detailLoadError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {detailLoadError}
            </Alert>
          ) : batchDetail ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.student', 'Ученик')}</TableCell>
                  <TableCell>{t('billing.shareAmount', 'Доля')}</TableCell>
                  <TableCell>{t('billing.lessonsRemainingColumn', 'Остаток')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchDetail.distributions.map((d: LessonSubscriptionGrantDistribution) => {
                  const student = d.grant?.contract?.student
                  const name =
                    student?.display_name ||
                    [student?.surname, student?.first_name].filter(Boolean).join(' ') ||
                    student?.email ||
                    `#${d.contract_id}`
                  const remaining = d.grant?.lessons_remaining
                  const total = d.grant?.lessons_total
                  return (
                    <TableRow key={d.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell>{formatMoney(d.share_amount)}</TableCell>
                      <TableCell>
                        {remaining != null && total != null
                          ? t('billing.lessonsRemaining', '{{remaining}} из {{total}}', {
                              remaining,
                              total,
                            })
                          : '—'}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>{t('common.close', 'Закрыть')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={priceDialogOpen} onClose={closePriceDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('billing.groupPriceDialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="number"
            margin="dense"
            label={t('billing.groupPricePerHourLabel')}
            value={priceValue}
            onChange={(event) => setPriceValue(event.target.value)}
            inputProps={{ min: 0, step: 50 }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('billing.groupPriceDialogHint')}
          </Typography>
          {priceError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {priceError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePriceDialog}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={priceSaving || priceValue === ''} onClick={() => void savePrice()}>
            {priceSaving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  )
}
